import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcMutualfundsV1VenueOrderQueryServiceVenueOrderGet } from '@interxfi/api-client/generated/hooks/ixc-mutualfunds-v1-venue-order-query-service-venue-order-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcMutualfundsV1VenueOrderState } from '@interxfi/types';
import {
  MutualFundsVenueOrderEventDrawer,
  MutualFundsVenueOrderEventSummary,
} from '../../../../../../../../../../features/mutual-funds/venue-order/mutual-funds-venue-order-event';
import { getMutualFundsVenueOrderStateListItems } from '../../../../../../../../../../features/mutual-funds/venue-order/mutual-funds-venue-order-list-items';
import { getMutualFundsVenueOrderDetailPath } from '../../../../../../../../../../features/mutual-funds/venue-order/mutual-funds-venue-order-routing';
import { MutualFundsVenueOrderStatusPill } from '../../../../../../../../../../features/mutual-funds/venue-order/mutual-funds-venue-order-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/mutualfunds/venue-order/_venue-order/$venueOrderId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `venue-order-${params.venueOrderId}`,
        label: 'Venue Order',
        to: getMutualFundsVenueOrderDetailPath(params.venueOrderId),
        suffix: <CopyButton content={params.venueOrderId} label="Copy Order ID" tooltip={{ copy: params.venueOrderId, copied: 'Copied Order ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Fund Order'),
  component: MutualFundsVenueOrderDetail,
});

function getListItems(data: IxcMutualfundsV1VenueOrderState | undefined) {
  const listItems = getMutualFundsVenueOrderStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          listItems.data.spec.action,
          ...('notional' in listItems.data.spec.amount ? [listItems.data.spec.amount.notional.value] : []),
          ...('quantity' in listItems.data.spec.amount ? [listItems.data.spec.amount.quantity.value] : []),
          listItems.data.venue,
          ...(listItems.data.settlementDate ? [listItems.data.settlementDate] : []),
          ...(listItems.data.failureReason ? [listItems.data.failureReason] : []),
        ],
      },
      ...(listItems.data.execution
        ? [
            {
              id: 'execution',
              title: 'Execution',
              items: [
                listItems.data.execution.executionId,
                listItems.data.execution.venue,
                listItems.data.execution.amount,
                listItems.data.execution.quantity,
                listItems.data.execution.nav,
                ...(listItems.data.execution.settlementTime ? [listItems.data.execution.settlementTime] : []),
              ],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [
      listItems.metadata.createdAt,
      listItems.data.spec.timestamp,
      listItems.venueOrderId,
      ...(listItems.data.venueBatchId ? [listItems.data.venueBatchId] : []),
      listItems.assetId,
      listItems.data.spec.accountId,
      listItems.data.spec.programId,
    ] as const satisfies ReadonlyListItem[],
  };
}

function getMenuItems(venueOrderId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: MutualFundsVenueOrderEventDrawer,
        props: { request: { venueOrderId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load venue order', 'view this venue order');

function MutualFundsVenueOrderDetail() {
  const { venueOrderId } = Route.useParams();
  const request = { venueOrderId } as const;
  const { data, error, isPending } = useIxcMutualfundsV1VenueOrderQueryServiceVenueOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.venueOrder), [data?.venueOrder]);
  const entityMenuItems = useMemo(() => getMenuItems(venueOrderId), [venueOrderId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <MutualFundsVenueOrderStatusPill isPending={showPending} size="large" status={data?.venueOrder?.status} />
            </HorizontalStack>

            <EntityActionMenu {...entityMenuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <MutualFundsVenueOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
